import _uniqBy from 'lodash/uniqBy';

export default class SensorsAndConnectorsController {
    /** @ngInject */
    constructor(
        EventEmitter,
        ThingsApi,
        Loader,
        ToastService,
        $state,
        $scope,
        $stateParams
    ) {
        this.EventEmitter = EventEmitter;
        this.ThingsApi = ThingsApi;
        this.Loader = Loader;
        this.ToastService = ToastService;
        this.$state = $state;
        this.$scope = $scope;
        this.$stateParams = $stateParams;

        this.performHealthCheck = this.performHealthCheck.bind(this);
        this.onUpdateReceived = this.onUpdateReceived.bind(this);
    }

    get currentThingId() {
        return this.$stateParams.thingId;
    }

    get isSideNavOpen() {
        return this.$state.current.name.indexOf('main.sensors.list.') === 0; // open if the sub-state active
    }

    get mainViewType() {
        return this.$state.current.name === 'main.sensors.list.thing.graph' ? 'GRAPH' : 'LIST';
    }

    $onChanges(changes) {
        if (changes.searchQuery && !changes.searchQuery.isFirstChange()) {
            this.loadDataByQuery(this.searchQuery);
        }
    }

    $onInit() {
        this.showUpdateButton = false;

        this.things = [];

        this.enableInfinteScroll = true;

        this.listMetadata = {
            query: this.searchQuery,
            order: null,
            page_size: 20
        };
        this.nextPageToken = '';
        this.selectedThings = {};

        this.loadList();

        this.$scope.$on('health-checker-page-visible', this.performHealthCheck);
        this.$scope.$on('health-checker-network-up', this.performHealthCheck);
    }

    $onDestroy() {
        this.unsubscribeFromLiveUpdates();
    }

    performHealthCheck() {
        if (this.liveDataSubscription && !this.liveDataSubscription.isAlive) {
            this.showWarning = true;
            this.loadList();
        }
    }

    thingUpdated({ thing }) {
        const thingRef = this.thingsMap[thing.id];
        if (thingRef) {
            if (thing.id === this.currentThingId && thing.starred !== thingRef.starred) {
                this.thingUpdate = thing;
            }

            ['starred', 'location', 'folder'].forEach((key) => {
                if (key in thing) {
                    thingRef[key] = thing[key];
                }
            });
        }
    }

    loadList(pageToken = '') {
        this.Loader.show();
        this.showUpdateButton = false;
        this.ThingsApi
            .getList({
                ...this.listMetadata,
                page_token: pageToken
            })
            .then((data) => {
                this.Loader.hide();
                this.nextPageToken = data.next_page_token;
                if (pageToken) {
                    this.onListUpdated(data);
                } else {
                    this.onListLoaded(data);
                }
                this.enableInfinteScroll = true;
            })
            .catch(() => {
                this.onLoadingError();
            })
            .finally(() => {
                this.Loader.hide();
            });
    }

    clearSearchResult() {
        this.search({
            query: null
        });
    }

    switchToNextPage() {
        this.offset += this.perPage;
    }

    onListReorder(order) {
        this.listMetadata.order = order;
        this.loadList();
    }

    onLoadingError() {
        this.ToastService.showSimpleTranslated('sensor_list_wasnt_loaded');
    }

    generateThingsMap() {
        this.thingsMap = {};
        const thingIds = [];
        this.things.forEach((thing) => {
            this.thingsMap[thing.id] = thing;
            thingIds.push(thing.id);
        });
        this.subscribeToLiveUpdates(thingIds);
        this.showWarning = false;
    }

    onListUpdated(data) {
        const newThings = data.things || [];
        this.things = _uniqBy([...this.things, ...newThings], 'id');
        this.generateThingsMap();
    }

    onListLoaded(data) {
        this.things = data.things || [];
        this.generateThingsMap();
        this.resetSelectedThings();
    }

    unsubscribeFromLiveUpdates() {
        if (this.liveDataSubscription) {
            this.liveDataSubscription.stop();
        }
    }

    subscribeToLiveUpdates(thingIds) {
        this.unsubscribeFromLiveUpdates();

        this.liveDataSubscription = this.ThingsApi.subscribeToAllUpdates(
            thingIds,
            this.onUpdateReceived
        );
    }

    static getUpdatedStateProps(thingType, oldProps, newProps) {
        const propsByType = {
            touch: 'last_pressed',
            temperature: 'temperature',
            proximity: 'object_present'
        };

        const result = {};

        const isTouchType = thingType === 'touch';

        const stateProp = propsByType[thingType];
        if (stateProp) {
            if (!isTouchType || (isTouchType && newProps.touch)) {
                if (oldProps[stateProp] !== newProps[stateProp]) {
                    result.state = true;
                }
            }
        }

        if (oldProps.battery_percentage !== newProps.battery_percentage) {
            result.battery_percentage = true;
        }

        if (oldProps.rssi_strength !== newProps.rssi_strength) {
            result.rssi_strength = true;
        }

        return result;
    }

    static getUpdatedProps(thing, patch) {
        if (patch.location) {
            return {
                location: true
            };
        }

        if (patch.name) {
            return {
                name: true
            };
        }

        if (patch.state && thing.state && thing.type) {
            return SensorsAndConnectorsController.getUpdatedStateProps(
                thing.type.id,
                thing.state.properties,
                patch.state.properties
            );
        }

        return {};
    }

    static animateUpdatedProps(thingId, props) {
        Object.keys(props).forEach((prop) => {
            const node = document.querySelector(`.cell-${thingId}-${prop}`);
            if (node) {
                node.classList.add('u-blink-default');
                setTimeout(() => {
                    node.classList.remove('u-blink-default');
                }, 2000);
            }
        });
    }

    onUpdateReceived(thingId, patch) {
        const thing = this.thingsMap[thingId];
        if (!thing) {
            return;
        }

        if (thing.id === this.currentThingId && patch.state) {
            this.lastEventReceived = patch;
        }

        const updatedProps = SensorsAndConnectorsController.getUpdatedProps(thing, patch);
        SensorsAndConnectorsController.animateUpdatedProps(thingId, updatedProps);

        if (patch.state) {
            if (!thing.state) {
                thing.state = {
                    properties: {}
                };
            }
            if (!patch.state.properties.touch) {
                patch.state.properties.last_pressed = thing.state.properties.last_pressed;
            }
            Object.assign(thing.state.properties, patch.state.properties);
            thing.state.updated = patch.state.updated;
        } else {
            Object.assign(thing, patch);
        }

        if (this.listMetadata.order) {
            this.showUpdateButton = true;
        }

        this.$scope.$applyAsync();
    }

    resetSelectedThings() {
        this.selectedThings = {
            isQuery: true,
            list: [],
            totalSelected: this.listMetadata.total
        };
    }

    onListEndReached() {
        if (!this.nextPageToken) {
            return;
        }

        this.loadList(this.nextPageToken);
    }

    search({ query }) {
        this.$state.go(this.$state.$current.name, {
            q: query,
            tabId: null
        });
    }

    loadDataByQuery(query) {
        this.listMetadata.query = query;
        this.loadList();
    }

}



// WEBPACK FOOTER //
// ./app/components/sensors-and-connectors/list/list.controller.js